import { HttpClient } from '@angular/common/http';
import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { environment } from '@app/environment/environment';
import { map, Observable, Observer, shareReplay } from 'rxjs';

export interface ImgData {
  imgUrl: SafeUrl;
}

@Injectable({
  providedIn: 'root',
})
export class ImgService {
  private readonly API_URL = environment.apiUrl + '/File';
  private imgCache: Map<string, ImgData> = new Map();
  private requestCache: Map<string, Observable<Blob>> = new Map();
  constructor(
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private destroyRef: DestroyRef,
  ) {}

  private callImageLoad(imgId: string): Observable<Blob> | undefined {
    if (this.requestCache.has(imgId)) {
      return this.requestCache.get(imgId);
    }

    const request = this.http
      .get(`${this.API_URL}/${imgId}`, { responseType: 'blob' })
      .pipe(shareReplay(1));

    this.requestCache.set(imgId, request);

    return request;
  }

  loadImage(imgId: string): Promise<SafeUrl | undefined> {
    if (this.imgCache.has(imgId)) {
      return Promise.resolve(this.imgCache.get(imgId)?.imgUrl);
    }

    return new Promise((resolve) => {
      this.callImageLoad(imgId)?.subscribe({
        next: (blob) => {
          const objectUrl = URL.createObjectURL(blob);
          const imgUrl = this.sanitizer.bypassSecurityTrustUrl(objectUrl);

          this.imgCache.set(imgId, { imgUrl });
          this.requestCache.delete(imgId);
          resolve(imgUrl);
        },
        error: () => {
          this.requestCache.delete(imgId);
          resolve(undefined);
        },
      });
    });
  }

  private createDataUrlObservable(blob: Blob): Observable<string> {
    return new Observable((observer: Observer<string>) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        observer.next(reader.result as string);
        observer.complete();
      };
      reader.onerror = (error) => {
        observer.error(error);
      };
      reader.readAsDataURL(blob);

      return () => {};
    });
  }
}
